import { Controller } from "@hotwired/stimulus"

// The phone's game screen. On a narrow viewport the room fills the screen and
// everything that stood beside or below it on a desktop — the care panel, the
// workshop, the phrasebook — waits in a sheet that slides up over the room when
// asked for. This controller only decides which sheet is up; the sheets
// themselves are the same panels the desktop renders, restyled by
// .ll-page--game[data-sheet=…] in the stylesheet. Nothing here runs on a wide
// screen: the dock that calls these actions is display:none there.
//
// The workshop is not a target: it lives inside the lair controller's scope
// and is looked up by class when needed, so the two controllers never share
// an element.
// Long enough to read a sentence, short enough that the next one is not
// stacked on it. Matches the room's own note (lair_controller.js, tell).
const TOAST_MS = 4200

export default class extends Controller {
  static targets = [ "aside" ]

  connect() {
    this.onKey = this.onKey.bind(this)
    this.onPick = this.onPick.bind(this)
    document.addEventListener("keydown", this.onKey)
    // Picking a piece from the workshop sheet is a promise to tap a tile next,
    // and the tile is under the sheet — so the pick closes it. Saying a phrase
    // from the Speak sheet closes it too: the bubble is over the room.
    this.element.addEventListener("click", this.onPick)
    this.watchToasts()

    // A homecoming is a present held out at the door. On a phone the door is
    // the Care sheet, so when there is one to show the sheet is raised once,
    // unasked, a beat after the room draws — it renders on the one request
    // that claims it, so this never fires twice for the same absence.
    // A trip home (Trips) is the same present, and a letter's link to the
    // trip panel is asking for that sheet too.
    const held = this.element.querySelector("#homecoming, #trip[data-trip='home']") ||
      (window.location.hash === "#trip" && this.element.querySelector("#trip"))
    if (this.phoneScreen() && held) {
      this.homecomingTimer = setTimeout(() => this.set("aside"), 700)
    }
  }

  disconnect() {
    clearTimeout(this.homecomingTimer)
    document.removeEventListener("keydown", this.onKey)
    this.element.removeEventListener("click", this.onPick)
    this.toastWatcher?.disconnect()
    this.toastFades?.forEach((timer) => clearTimeout(timer))
  }

  // On a phone the room's replies — a care act's sentence — sit as toasts
  // over the room's foot (see the stylesheet). Nothing dismissed them, so
  // "You feed the fire" was still up ten minutes later. Clear them after a
  // reading's worth, the way the room's own note is cleared. The redirect
  // flash beside them is its own toast and leaves by itself (flash_controller);
  // emptying it from here left its frame behind, a bordered blank over the room.
  watchToasts() {
    if (!this.phoneScreen()) return

    const flash = this.element.querySelector("#lair-flash")
    if (!flash) return

    this.toastWatcher = new MutationObserver(() => {
      if (flash.querySelector(".ll-flash")) this.fadeToast(flash)
    })
    this.toastWatcher.observe(flash, { childList: true })
  }

  fadeToast(node) {
    this.toastFades ||= new Map()
    clearTimeout(this.toastFades.get(node))
    this.toastFades.set(node, setTimeout(() => {
      node.replaceChildren()
      this.toastFades.delete(node)
    }, TOAST_MS))
  }

  phoneScreen() {
    return window.matchMedia?.("(max-width: 47.99rem)").matches === true
  }

  openAside(event) {
    event?.preventDefault()
    this.toggle("aside")
  }

  // The venue's Work panel sits in the same sheet as the phrasebook, below
  // it; this raises the sheet already scrolled to the quay. Asking again
  // while it is up closes the sheet, like every other dock button.
  openWork(event) {
    event?.preventDefault()
    if (this.element.dataset.sheet === "aside") return this.set(null)
    this.set("aside")
    requestAnimationFrame(() => {
      this.element.querySelector("#work")?.scrollIntoView({ block: "start" })
    })
  }

  // The roads out: a list of signposts, for a thumb.
  openRoads(event) {
    event?.preventDefault()
    this.toggle("roads")
  }

  openRoom(event) {
    event?.preventDefault()
    const open = this.toggle("room")
    // The first time the sheet opens, arranging is what it is for: the
    // workshop's own "Arrange the lair" button is inside the sheet, so the
    // dock presses it on the keeper's behalf rather than asking twice.
    if (!open) return
    const toggle = this.element.querySelector("[data-lair-target='arrangeToggle']")
    const arranging = this.element.querySelector(".ll-scene--arranging")
    if (toggle && !arranging) toggle.click()

    // And open on the storage itself. The sheet is short — under three
    // quarters of the screen — and the purse line, the hint and the filter
    // bar took all of it, so "Room" showed a keeper everything but their
    // pieces (Saoud: "why can't I access my storage quickly in my ROOM
    // tab?"). The bar is sticky at the sheet's top, so the heading is set
    // just under it rather than scrolled to the edge and hidden behind it.
    requestAnimationFrame(() => this.scrollSheetTo("#lair-storage"))
  }

  // Scroll the sheet that is up so `selector` sits just under its sticky
  // filter bar, if it has one. The sheet is its own scroller (position:
  // fixed, overflow-y: auto) and the offsetParent of everything in it.
  scrollSheetTo(selector) {
    const heading = this.element.querySelector(selector)
    if (!heading) return
    const sheet = heading.closest(".ll-workshop, .ll-hearth__aside, .ll-roads")
    if (!sheet) return
    const bar = sheet.querySelector(".ll-shop-filters")
    const clearance = (bar?.offsetHeight || 0) + 8
    sheet.scrollTo({ top: Math.max(0, heading.offsetTop - clearance) })
    // Once the bar is stuck it sits inside the sheet's padding, so correct
    // by what is actually on screen rather than by offsets.
    if (!bar) return
    const overlap = bar.getBoundingClientRect().bottom + 8 - heading.getBoundingClientRect().top
    if (overlap > 0) sheet.scrollTo({ top: Math.max(0, sheet.scrollTop - overlap) })
  }

  close(event) {
    event?.preventDefault()
    this.set(null)
  }

  // Which sheet is up, or null. Asking for the sheet already up closes it, so
  // the dock buttons work as toggles without carrying state of their own.
  toggle(name) {
    const next = this.element.dataset.sheet === name ? null : name
    this.set(next)
    return next !== null
  }

  set(name) {
    if (name) {
      this.element.dataset.sheet = name
    } else {
      delete this.element.dataset.sheet
    }
    this.element.querySelectorAll("[data-game-shell-sheet]").forEach((button) => {
      button.setAttribute("aria-expanded", String(button.dataset.gameShellSheet === name))
    })
  }

  onKey(event) {
    if (event.key === "Escape" && this.element.dataset.sheet) this.set(null)
  }

  onPick(event) {
    const sheet = this.element.dataset.sheet
    if (sheet === "room") {
      // A storage card picked up drops the sheet so the tile can be tapped. A
      // shop card is a purchase, not a pick: the sheet stays up, with the
      // receipt's own "place it" to drop it if that is what is wanted.
      const item = event.target.closest(".ll-crate-item")
      if (item && !item.closest(".ll-crate-slot--empty") && !item.classList.contains("ll-crate-item--shop")) this.set(null)
      // A paint swatch is a try-on, and the walls it goes on are under the
      // sheet — so the tap drops the sheet to show the coat, with the keep
      // bar (and its bill) over the room. The mantel's keepsake chips share
      // the class and stay: they change a shelf, not the room.
      const swatch = event.target.closest(".ll-swatch[data-kind]")
      if (swatch) this.set(null)
      return
    }
    if (sheet === "aside" && event.target.closest(".ll-btn--phrase, [role='group'][aria-label='Emotes'] .ll-btn")) {
      this.set(null)
    }
  }
}
